import { useSidebar } from "@elit-hair/elit-ui";
import { LayoutGroup, motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import { ChatHeader } from "@/components/ai-chat/chat-header";
import { ChatInput } from "@/components/ai-chat/chat-input";
import { ChatMessages } from "@/components/ai-chat/chat-messages";
import { DarwinAvatar } from "@/components/ai-chat/darwin-avatar";
import { DarwinEmptyStateProps } from "@/components/ai-chat/types/ai-chat-panel.types";
import { useChatStream } from "@/components/ai-chat/use-chat-stream";
import { useElitaiConnection } from "@/components/ai-chat/use-elitai-connection";
import {
    DARWIN_AVATAR_LAYOUT_ID,
    DARWIN_AVATAR_LAYOUT_TRANSITION,
    DARWIN_AVATAR_SIZE,
    DARWIN_EMPTY_STATE_AVATAR_PADDING,
} from "@/constants/ai-chat";
import { useRequestDarwinTool, useSendDarwinRating } from "@/hooks/queries/feedback.queries";
import { cn } from "@/lib/utils";
import { ChatMessage, ChatMessageRating } from "@/types/chatbot.types";
import { DarwinAvatarState } from "@/types/darwin-avatar.types";

function getAvatarState(isBusy: boolean, hasInput: boolean): DarwinAvatarState {
    if (isBusy) {
        return "waiting";
    }

    if (hasInput) {
        return "input";
    }

    return "idle";
}

function DarwinUnavailableNotice({ centered = false }: { centered?: boolean }) {
    const { t } = useTranslation();

    return (
        <div
            className={cn(
                "rounded-2xl bg-gradient-to-br from-violet-500/15 via-fuchsia-500/10 to-cyan-500/15 px-4 py-3 ring-1 ring-violet-500/25 dark:ring-white/10",
                centered
                    ? "mx-auto mt-3 flex max-w-[16rem] flex-col items-center gap-2"
                    : "mx-3 mt-3 flex items-center gap-3"
            )}
        >
            <span className="relative flex size-2.5 shrink-0">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-violet-400/70" />
                <span className="relative inline-flex size-2.5 rounded-full bg-violet-500 shadow-[0_0_10px] shadow-violet-500/70" />
            </span>
            <div className={cn("min-w-0", centered && "text-center")}>
                <p className="text-sm font-medium text-foreground">{t("darwin_unavailable")}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{t("darwin_unavailable_hint")}</p>
            </div>
        </div>
    );
}

function DarwinEmptyState({ avatarState, showAvatar, unavailable }: DarwinEmptyStateProps) {
    const { t } = useTranslation();
    const avatarFrameSize = DARWIN_AVATAR_SIZE.EMPTY_STATE + DARWIN_EMPTY_STATE_AVATAR_PADDING * 2;

    return (
        <div className="flex h-full min-h-0 flex-col items-center justify-center gap-5 overflow-visible px-6 py-8 text-center">
            <div
                className="relative flex items-center justify-center overflow-visible"
                style={{ width: avatarFrameSize, height: avatarFrameSize }}
            >
                <motion.div
                    aria-hidden
                    className={cn(
                        "absolute size-48 rounded-full blur-3xl",
                        unavailable
                            ? "bg-violet-500/10 dark:bg-violet-400/10"
                            : "bg-violet-500/25 dark:bg-fuchsia-400/20"
                    )}
                    initial={false}
                    animate={{ opacity: showAvatar ? 1 : 0 }}
                    transition={{ duration: 0.2 }}
                />
                {showAvatar ? (
                    <motion.div
                        layoutId={DARWIN_AVATAR_LAYOUT_ID}
                        transition={DARWIN_AVATAR_LAYOUT_TRANSITION}
                        className="relative flex items-center justify-center overflow-visible"
                    >
                        <DarwinAvatar size={DARWIN_AVATAR_SIZE.EMPTY_STATE} state={avatarState} />
                    </motion.div>
                ) : null}
            </div>
            <motion.div
                className="space-y-1"
                initial={false}
                animate={{ opacity: showAvatar ? 1 : 0, y: showAvatar ? 0 : 8 }}
                transition={{ duration: 0.2 }}
            >
                <p className="text-base font-semibold text-foreground">{t("darwin")}</p>
                {unavailable ? (
                    <DarwinUnavailableNotice centered />
                ) : (
                    <p className="text-sm text-muted-foreground">{t("how_can_i_help_you")}</p>
                )}
            </motion.div>
        </div>
    );
}

export function AiChatPanel() {
    const { t } = useTranslation();
    const { toggleSidebar, open, openMobile, isMobile } = useSidebar();
    const isChatOpen = isMobile ? openMobile : open;
    const sendRating = useSendDarwinRating();
    const requestTool = useRequestDarwinTool();
    const { isDisconnected } = useElitaiConnection();

    const {
        messages,
        inputRef,
        defaultInput,
        hasInput,
        inputRevision,
        handleInputChange,
        handleSubmit,
        clearHistory,
        rateMessage,
        markToolRequested,
        conversationId,
        isLoading,
        isStreaming,
        stopStreaming,
    } = useChatStream();

    const hasMessages = messages.length > 0;
    const isBusy = isLoading || isStreaming;
    const avatarState = getAvatarState(isBusy, hasInput);

    const handleRequestTool = (message: ChatMessage) => {
        if (!conversationId || message.toolRequest?.incidentId) return;
        requestTool.mutate(
            { conversationId, messageId: message.id },
            { onSuccess: ({ incidentId }) => markToolRequested({ messageId: message.id, incidentId }) }
        );
    };

    const handleRate = ({ message, rating }: { message: ChatMessage; rating?: ChatMessageRating }) => {
        rateMessage({ messageId: message.id, rating });
        if (conversationId) {
            sendRating.mutate({ conversationId, messageId: message.id, rating });
        }
    };

    return (
        <LayoutGroup id="darwin-chat-panel">
            <div className="darwin-chat-panel flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-border/80 bg-background/40 shadow-inner dark:border-white/15">
                <div className="relative z-10 flex h-full min-h-0 flex-col">
                    <ChatHeader
                        avatarState={avatarState}
                        showAvatar={hasMessages}
                        onClose={toggleSidebar}
                        onClearHistory={clearHistory}
                    />

                    {isDisconnected && hasMessages ? <DarwinUnavailableNotice /> : null}

                    <div className="relative min-h-0 flex-1 overflow-hidden">
                        <div
                            className={cn(
                                "absolute inset-0 overflow-visible transition-opacity duration-200",
                                hasMessages ? "pointer-events-none opacity-0" : "opacity-100"
                            )}
                            aria-hidden={hasMessages}
                        >
                            <DarwinEmptyState
                                avatarState={avatarState}
                                showAvatar={!hasMessages}
                                unavailable={isDisconnected}
                            />
                        </div>

                        {hasMessages ? (
                            <div className="absolute inset-0 overflow-hidden">
                                <ChatMessages
                                    messages={messages}
                                    isLoading={isBusy}
                                    onRate={handleRate}
                                    onRequestTool={handleRequestTool}
                                    requestingToolMessageId={
                                        requestTool.isPending ? requestTool.variables?.messageId : undefined
                                    }
                                />
                            </div>
                        ) : null}
                    </div>

                    <div className="shrink-0 border-t border-border/80 bg-background/50 backdrop-blur-md dark:border-white/15">
                        <ChatInput
                            inputRef={inputRef}
                            defaultValue={defaultInput}
                            onValueChange={handleInputChange}
                            inputRevision={inputRevision}
                            active={isChatOpen}
                            onSubmit={handleSubmit}
                            isLoading={isLoading}
                            isStreaming={isStreaming}
                            onStop={stopStreaming}
                            placeholder={hasMessages ? t("type_your_message") : t("ask_me_anything")}
                            useTypewriterPlaceholder={!hasMessages}
                            className="chatbot-input"
                        />
                    </div>
                </div>
            </div>
        </LayoutGroup>
    );
}
